import { inject, Injectable } from '@angular/core';
import { GridStack, GridStackElement, GridStackWidget } from 'gridstack';
import { EntityId } from '@ngrx/signals/entities';
import { DataGroupProperty } from '../stores/indicatpr-data/indicator-data.widget-selectors';
import { ChartData } from 'chart.js';
import { KeyValueDistricts } from '../models/geo-data';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LatLng } from 'leaflet';
import { ComparisonValueStore } from '../stores/comparison-value/comparison-value.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { BaseIndicator, Indicator } from '../models/indicator';
import { GlobalsStore } from '../stores/globals.state';
import { MapLegendPosition, MapLegendScope } from '../models/commune';
import { DropdownItem } from '../models/form/basic';
import { ImageStore } from '../stores/image/image.state';
import { Image } from '../models/image';
import { getUploadPath } from '../utility/app.utility';

export enum ChartBehavior {
  SINGLE_COLOR_PER_SEGMENT, // Pie, Doughnut, Polar Area
  SINGLE_COLOR_PER_DATASET, // Bar, Line, Radar, Scatter, Bubble
}

export enum WidgetBehavior {
  DATA_WIDGET,
  CONTENT_WIDGET,
  PLACEHOLDER,
}

export enum ChartAxisBehavior {
  DATA_AXIS_BOTH,
  DATA_AXIS_SINGLE,
  DATA_AXIS_NONE,
}

export enum ChartInteractionBehavior {
  POINT,
  SHAPE,
}

export type WidgetConfigStep = 'indicators' | 'data' | 'customize';
export type LegendPositions = 'top' | 'right' | 'bottom' | 'left' | 'none';

export type RegionYearValues = number[];

export interface WegweiserIndicator extends BaseIndicator {
  type: 'wegweiser'; // discriminant
  id: number;
  name: string;
  title: string;
  calculation: string;
  source: string;
  friendlyUrl: string;
  regionYearValues: RegionYearValues[];
}

export type WegweiserRegion = {
  name: string;
  id: number;
  title: string;
  friendlyUrl: string;
  type: string;
};

export type WegweiserData = {
  regions: WegweiserRegion[];
  indicators: WegweiserIndicator[];
  years: number[];
  geoData: any;
};

export type DataContext = 'local' | 'external';

export const chartBehaviorMap: Record<string, ChartBehavior> = {
  pie: ChartBehavior.SINGLE_COLOR_PER_SEGMENT,
  doughnut: ChartBehavior.SINGLE_COLOR_PER_SEGMENT,
  polarArea: ChartBehavior.SINGLE_COLOR_PER_SEGMENT,
  bar: ChartBehavior.SINGLE_COLOR_PER_DATASET,
  line: ChartBehavior.SINGLE_COLOR_PER_DATASET,
  radar: ChartBehavior.SINGLE_COLOR_PER_DATASET,
  scatter: ChartBehavior.SINGLE_COLOR_PER_DATASET,
  bubble: ChartBehavior.SINGLE_COLOR_PER_DATASET,
};

export const chartInteractionBehaviorMap: Record<
  string,
  ChartInteractionBehavior
> = {
  pie: ChartInteractionBehavior.SHAPE,
  doughnut: ChartInteractionBehavior.SHAPE,
  polarArea: ChartInteractionBehavior.SHAPE,
  bar: ChartInteractionBehavior.SHAPE,
  line: ChartInteractionBehavior.POINT,
  radar: ChartInteractionBehavior.POINT,
  scatter: ChartInteractionBehavior.POINT,
  bubble: ChartInteractionBehavior.SHAPE,
};

export const widgetBehaviorMap: Record<string, WidgetBehavior> = {
  adder: WidgetBehavior.PLACEHOLDER,
  map: WidgetBehavior.DATA_WIDGET,
  pie: WidgetBehavior.DATA_WIDGET,
  doughnut: WidgetBehavior.DATA_WIDGET,
  polarArea: WidgetBehavior.DATA_WIDGET,
  bar: WidgetBehavior.DATA_WIDGET,
  line: WidgetBehavior.DATA_WIDGET,
  radar: WidgetBehavior.DATA_WIDGET,
  scatter: WidgetBehavior.DATA_WIDGET,
  bubble: WidgetBehavior.DATA_WIDGET,
  table: WidgetBehavior.DATA_WIDGET,
  info: WidgetBehavior.DATA_WIDGET,
  text: WidgetBehavior.CONTENT_WIDGET,
  teaser: WidgetBehavior.CONTENT_WIDGET,
};

export const chartAxisBehaviorMap: Record<string, ChartAxisBehavior> = {
  pie: ChartAxisBehavior.DATA_AXIS_NONE,
  doughnut: ChartAxisBehavior.DATA_AXIS_NONE,
  polarArea: ChartAxisBehavior.DATA_AXIS_NONE,
  bar: ChartAxisBehavior.DATA_AXIS_SINGLE,
  line: ChartAxisBehavior.DATA_AXIS_SINGLE,
  radar: ChartAxisBehavior.DATA_AXIS_NONE,
  scatter: ChartAxisBehavior.DATA_AXIS_BOTH,
  bubble: ChartAxisBehavior.DATA_AXIS_BOTH,
};

export interface CustomGridStackWidget extends Omit<GridStackWidget, 'id'> {
  id: EntityId;
  _id?: any; // workaround
  type: WidgetType;
  widget: WidgetConfig;
  component?: any;
  fullscreen?: boolean;
  el?: GridStackElement;
  grid?: GridStack;
}

export type WegweiserRequestParams = {
  indicatorIds: number[];
  regionIds: number[];
  years: number[];
};

export interface WidgetConfig {
  context: DataContext;
  wegweiserRequestParams: WegweiserRequestParams;
  wegweiserData: null | WegweiserData;
  headline: string;
  chartData?: ChartData | null | any; // todo replace any with matching chart.js type
  groupedData?: null | any; // todo replace any with matching chart.js type
  axisBasedData?: null | any; // todo replace any with matching chart.js type
  axisProperties: string[];
  showFullIndicatorName: boolean;
  disabledCharts: string[];
  dotSize: number;
  featureOpacity: number;
  borderWidth?: number;
  innerRadius: number;
  lineTension: number;
  radiusProperty: string;
  year: string;
  dataLayer: string;
  dataLayers?: string[];
  dataLayerType: string;
  indicators: string[];
  indicator: string;
  feature: string;
  features?: KeyValueDistricts | null;
  colorset: string;
  invertColors: boolean;
  legendPosition: LegendPositions;
  disabledFeatures: string[];
  disabledYears: number[];
  locked: boolean;
  focusProperty?: DataGroupProperty;
  groupProperty?: DataGroupProperty;
  dataSetProperty?: DataGroupProperty;
  mode: ChartMode;
  stacked: boolean;
  displayValues: boolean;
  displayRegression?: boolean;
  xAxisLabel?: string; // for chart-options
  yAxisLabel?: string; // for chart-options
  initialized?: boolean;
  activeGroup: string;
  comparisonValues: string[];
  disabledDatasets: string[];
  disabledDataPoints: string[];
  axis?: {
    x: {
      max: number;
      min: number;
      label: string;
    };
    y: {
      max: number;
      min: number;
      label: string;
    };
  };
  sort: ChartSorting;
  map?: {
    tileLayer: string;
    districtOpacity: number;
    poiSize: number;
    colorset: string;
    captions: {
      key: boolean;
      name: boolean;
      value: boolean;
      chart: boolean;
      style?: string;
    };
    //poi?: Record<string, string[] | null>;
    poi?: PoiConf[];
    activeIsochrones?: string[];
    isochroneProfile?: 'driving' | 'walking' | 'cycling';
    isochroneMinutes?: number[];
    isochroneColorset?: string;
    groups?: Record<string, string | null>;
    legend: {
      legendScope: MapLegendScope;
      position: MapLegendPosition;
      scaleMode: ScaleMode;
      size: LegendSize;
      numberOfScales?: number;
      invert: boolean;
    };
    view: {
      zoom: any;
      bounds?: LatLng;
    };
  };
  text?: {
    content: string;
    alignment: 'left' | 'top' | 'bottom' | 'right' | null;
    backgroundColor: string;
  };
  table?: {
    transpose: boolean;
    displayKeys: boolean;
    captionTotal: string;
    captionAverage: string;
    captionDistrict: string;
    captionKey: string;
  };
  teaser?: { items: TeaserItem[]; autoPlay?: boolean };
}

export interface PoiConf {
  type: string;
  indicators?: string[];
  legend?: {
    invert?: boolean;
    legendScope?: MapLegendScope;
    scaleMode?: ScaleMode;
    numberOfScales?: number;
    colorset: string;
  };
}

export interface TeaserItem {
  topmark: string;
  header: string;
  content: string;
  backgroundId?: string;
  backgroundColor: string;
  backgroundSrc?: string;
  src: string;
  copyright: string;
  type: 'story' | 'bookmark' | 'topic' | 'link';
  bookmark?: string;
  story?: string;
  topic?: string;
  linkCaption: string;
  href: string;
}

export interface TeaserImage {
  _id: string;
  src: string;
  copyright: string;
}

export interface ScatterData {
  x: number;
  y: number;
  r: number | null;
}

export interface RegressionData {
  line: { x: number; y: number }[];
  r2: number;
}

export interface PoiChartData {
  value: number;
  color: string;
  indicator: Indicator;
}

export type ChartMode = 'horizontal' | 'vertical';
export type ScaleMode = 'width' | 'size';
export type LegendSize = 'large' | 'small' | 'none';

export interface ChartSorting {
  enable: boolean;
  property: 'label' | 'value';
  direction: 'asc' | 'desc';
}

export const defaultSorting: ChartSorting = {
  enable: false,
  property: 'label',
  direction: 'asc',
};

export type ChartType =
  | 'map'
  | 'scatter'
  | 'bubble'
  | 'bar'
  | 'line'
  | 'pie'
  | 'table'
  | 'polarArea'
  | 'radar';

export type WidgetType = ChartType | 'adder' | 'text' | 'teaser' | 'info';

export const WIDGET_TYPE_DISPLAY: Partial<
  Record<WidgetType, { icon: string; label: string }>
> = {
  map: { icon: 'fas fa-map-location-dot', label: 'Karte' },
  bar: { icon: 'fas fa-chart-column', label: 'Balken-/Säulendiagramm' },
  line: { icon: 'fas fa-chart-line', label: 'Liniendiagramm' },
  pie: { icon: 'fas fa-chart-pie', label: 'Kreis-/Ringdiagramm' },
  scatter: { icon: 'fas fa-arrow-up-right-dots', label: 'Streudiagramm' },
  bubble: { icon: 'fas fa-circle', label: 'Blasendiagramm' },
  radar: { icon: 'fas fa-satellite-dish', label: 'Netz-/Radardiagramm' },
  polarArea: { icon: 'fas fa-snowflake', label: 'Polardiagramm' },
  table: { icon: 'fas fa-table', label: 'Tabelle' },
  text: { icon: 'fas fa-font', label: 'Text' },
  info: { icon: 'fas fa-info-circle', label: 'Indikatorinfo' },
  teaser: { icon: 'fas fa-pager', label: 'Teaser/Bild' },
};

export const mapLegendOptions = [
  {
    label: 'vertikal',
    value: 'large',
  },
  {
    label: 'horizontal',
    value: 'small',
  },
  {
    label: 'ausblenden',
    value: 'none',
  },
];

export const chartLegendOptions = [
  {
    label: 'oben',
    value: 'top',
  },
  {
    label: 'rechts',
    value: 'right',
  },
  {
    label: 'unten',
    value: 'bottom',
  },
  {
    label: 'links',
    value: 'left',
  },
  {
    label: 'ausblenden',
    value: 'none',
  },
];

export const mapLegendPositionOptions = [
  {
    label: 'oben-links',
    value: 'topleft',
  },
  {
    label: 'oben-rechts',
    value: 'topright',
  },
  {
    label: 'unten-links',
    value: 'bottomleft',
  },
  {
    label: 'unten-rechts',
    value: 'bottomright',
  },
];

export const mapTooltipStyleDropdownItems: DropdownItem[] = [
  { label: 'Oben', value: 'top', icon: 'fas fa-chevron-up' },
  { label: 'Rechts', value: 'right', icon: 'fas fa-chevron-right' },
  { label: 'Unten', value: 'bottom', icon: 'fas fa-chevron-down' },
  { label: 'Links', value: 'left', icon: 'fas fa-chevron-left' },
  { label: 'Zentriert', value: 'center', icon: 'fas fa-arrows-to-dot' },
  { label: 'Automatisch', value: 'auto', icon: 'fas fa-shuffle' },
  { label: 'Transparent', value: 'blank', icon: 'fas fa-rectangle-xmark' },
];

@Injectable({
  providedIn: 'root',
})
export class GridWidgetConfigService {
  readonly globalsStore = inject(GlobalsStore);
  readonly imageStore = inject(ImageStore);
  readonly comparisonStore = inject(ComparisonValueStore);
  readonly layerStore = inject(DataLayerStore);

  constructor(private fb: FormBuilder) {}

  /**
   * Generates a configuration object for a grid item by merging default settings
   * with custom configurations and widget-specific properties.
   *
   * @param {Partial<CustomGridStackWidget>} customConfig - Partial configuration object to override default grid item settings.
   * @param {Partial<WidgetConfig>} widgetConfig - Partial configuration object to provide widget-specific properties.
   * @param {WidgetType} [type] - Optional type of the widget to determine the specific configuration.
   * @return {CustomGridStackWidget} A complete configuration object for the grid item.
   */
  getGridItemConfig(
    customConfig: Partial<CustomGridStackWidget> = {},
    widgetConfig: Partial<WidgetConfig> = {},
    type?: WidgetType
  ): CustomGridStackWidget {
    const now = Date.now();
    const defaultConfig: any = {
      w: 4,
      h: 2,
      _id: now,
      id: now,
      draggable: true,
      type: type || 'adder',
      widget: this.getWidgetConfig(widgetConfig, type),
    };

    return { ...defaultConfig, ...customConfig };
  }

  /**
   * Generates and returns a template object of type WegweiserData with default values.
   *
   * @return {WegweiserData} An object containing the default structure for WegweiserData,
   * including empty arrays for regions, indicators, and years, and a null value for geoData.
   */
  getWegweiserDataTemplate(): WegweiserData {
    return {
      regions: [],
      indicators: [],
      years: [],
      geoData: null,
    };
  }

  /**
   * Generates and returns the configuration for a widget by merging base, type-specific, and custom configurations.
   *
   * @param {Partial<WidgetConfig>} customConfig - An optional partial configuration object to override the default configuration.
   * @param {WidgetType} [type] - An optional type of the widget, used to determine the type-specific configuration.
   * @return {WidgetConfig} The fully resolved widget configuration object.
   */
  getWidgetConfig(
    customConfig: Partial<WidgetConfig> = {},
    type?: WidgetType
  ): WidgetConfig {
    const baseConfig = this.globalsStore.getBaseConfig();
    const communeConfig = this.globalsStore.communeConfiguration() || null;

    let typeConfig = null;
    if (type && communeConfig && type in communeConfig) {
      typeConfig = communeConfig[type] || baseConfig[type] || null;
    }

    // Forecast years must never be pre-selected: a newly created widget
    // starts with its forecast years disabled until the user opts in.
    const forecastYears = customConfig.dataLayerType
      ? this.layerStore
          .filterByParent(customConfig.dataLayerType)
          .filter(layer => layer.isForecast)
          .map(layer => layer.year)
      : [];

    const defaultConfig: WidgetConfig = {
      context: 'local',
      wegweiserRequestParams: {
        regionIds: [],
        indicatorIds: [],
        years: [],
      },
      wegweiserData: null,
      headline: '',
      chartData: null,
      groupedData: null,
      axisBasedData: null,
      showFullIndicatorName: true,
      axisProperties: [],
      disabledCharts: typeConfig?.disabledCharts || [],
      dotSize: typeConfig?.dotSize || 3,
      featureOpacity: typeConfig?.featureOpacity ?? 0.5,
      borderWidth: 2,
      lineTension: typeConfig?.lineTension || 0,
      radiusProperty: '',
      innerRadius: typeConfig?.innerRadius || 0,
      year: '',
      dataLayer: '',
      dataLayers: [],
      dataLayerType: '',
      indicators: [],
      indicator: '',
      feature: '',
      features: null,
      colorset: typeConfig?.colorset || communeConfig?.colorset || null,
      invertColors: false,
      legendPosition: 'right',
      disabledFeatures: this.comparisonStore.keys() || ['total', 'avg'], // doof wenn neuer comp-eintrag hinzukommt... der wird dann immer direkt überall angezeigt --> ziemlich kacke
      disabledYears: forecastYears,
      locked: false,
      focusProperty: 'dataLayer',
      groupProperty: 'feature',
      dataSetProperty: 'indicator',
      mode: 'vertical',
      stacked: false,
      displayValues: typeConfig?.displayValues || false,
      initialized: false,
      activeGroup: '',
      sort: typeConfig?.sort || defaultSorting,
      table: typeConfig?.table || null,
      comparisonValues: [],
      disabledDatasets: typeConfig?.disabledDatasets || [],
      disabledDataPoints: typeConfig?.disabledDataPoints || [],
    };

    return {
      ...defaultConfig,
      ...this.getConfigByType(typeConfig, type),
      ...customConfig,
    };
  }

  /**
   * Retrieves the configuration for a widget based on its type.
   *
   * @param typeConfig Configuration object specific to the widget type, or null if no configuration is provided.
   * @param type Optional string specifying the type of the widget (e.g., 'map', 'bar', 'line', etc.).
   * @return A partial configuration object for the specified widget type, based on the provided typeConfig parameter.
   */
  getConfigByType(
    typeConfig: any | null,
    type?: string
  ): Partial<WidgetConfig> {
    switch (type) {
      case 'map':
        return {
          map: {
            tileLayer: typeConfig?.tileLayer || 'Graustufen',
            districtOpacity: typeConfig?.districtOpacity || 0.8,
            poiSize: typeConfig?.poiSize,
            colorset: typeConfig?.colorset || null,
            captions: {
              key: typeConfig?.captions?.key || false,
              name: typeConfig?.captions?.name || false,
              value: typeConfig?.captions?.value || false,
              chart: typeConfig?.captions?.chart || false,
              style: typeConfig?.captions?.style || 'top',
            },
            legend: {
              legendScope:
                typeConfig?.legend?.legendScope || ('group' as MapLegendScope),
              scaleMode:
                typeConfig?.legend?.scaleMode || ('width' as ScaleMode),
              size: typeConfig?.legend?.size || ('small' as LegendSize),
              position: typeConfig?.legend?.position || 'bottomright',
              numberOfScales: typeConfig?.legend?.numberOfScales || 6,
              invert: false,
            },
            isochroneMinutes: typeConfig?.isochroneMinutes || [15, 30, 45],
            view: {
              zoom: undefined,
            },
          },
        };
      case 'bar':
        return {
          displayValues: typeConfig?.displayValues || false,
          //caption: typeConfig?.caption || 'name',
          colorset: typeConfig?.colorset || false,
          stacked: typeConfig?.stacked || false,
          mode: typeConfig?.mode || 'vertical',
          sort: typeConfig?.sort || defaultSorting,
        };
      case 'line':
        return {
          focusProperty: 'indicator',
          groupProperty: 'dataLayer',
          sort: {
            enable: true,
            direction: 'asc',
            property: 'label',
          },
        };
      case 'pie':
        return {
          sort: typeConfig?.sorting || defaultSorting,
        };
      case 'table':
        return {
          table: {
            transpose: typeConfig?.transpose || false,
            displayKeys: typeConfig?.displayKeys || false,
            captionTotal: typeConfig?.captionTotal || '',
            captionAverage: typeConfig?.captionAverage || '',
            captionDistrict: typeConfig?.captionDistrict || '',
            captionKey: typeConfig?.captionKey || '',
          },
        };
      case 'bubble':
      case 'scatter':
      case 'polarArea':
        return {};
      case 'text':
        return {
          text: {
            content: typeConfig?.content || '',
            alignment: typeConfig?.alignment || null,
            backgroundColor: typeConfig?.backgroundColor || '#ffffff',
          },
        };
      case 'info':
      case 'teaser':
      default:
        return {};
    }
  }

  /**
   * Retrieves the indicator mode for a given widget type.
   *
   * @param {WidgetType} type - The type of widget for which to get the indicator mode.
   * @return {string} The indicator mode associated with the specified widget type.
   */
  getIndicatorModeByType(type: WidgetType): string {
    return this.typeConfig[type]['indicatorMode'];
  }

  /**
   * Retrieves the data layer fields for a specific widget type.
   *
   * @param {WidgetType} type - The widget type for which the data layer fields are to be retrieved.
   * @return {any} The data layer fields corresponding to the specified widget type.
   */
  getDataLayerFieldsByType(type: WidgetType): any {
    return this.typeConfig[type]['dataLayerFields'];
  }

  /**
   * Retrieves the icon/label display info for a given widget type, used for
   * type-selection UI (speed-dial) and the arrange-mode placeholder.
   *
   * @param {WidgetType} type - The widget type to look up.
   * @return {{ icon: string; label: string } | undefined} Display info, if defined for the type.
   */
  getTypeDisplay(
    type: WidgetType
  ): { icon: string; label: string } | undefined {
    return WIDGET_TYPE_DISPLAY[type];
  }

  /**
   * Retrieves form fields for a specific widget type and constructs a FormGroup based on these fields.
   *
   * @param {WidgetType} type - The type of the widget for which form fields are to be retrieved.
   * @param {any} config - The configuration object used to initialize the form fields.
   * @return {FormGroup} A FormGroup object containing the form controls for the specified widget type.
   */
  getFormFieldsByType(type: WidgetType, config: any): FormGroup<any> {
    const formGroupConfig: Record<string, AbstractControl> = {};
    const typeFields = this.typeConfig[type]?.configFields || [];
    for (const field of typeFields) {
      formGroupConfig[field] = this.getConfigFieldByName(field, config); // Add default validators as needed
    }

    return this.fb.group(formGroupConfig, { updateOn: 'change' });
  }

  // todo: add chart-options?
  typeConfig: Record<WidgetType, any> = {
    map: {
      indicatorMode: 'multiple',
      dataLayerFields: ['poi'],
      configFields: ['headline', 'colorset', 'map', 'comparisonValues'],
    },
    table: {
      indicatorMode: 'multiple',
      dataLayerFields: ['group', 'disable'],
      configFields: ['headline', 'table', 'sort', 'showFullIndicatorName'],
    },
    bar: {
      indicatorMode: 'multiple',
      dataLayerFields: ['group', 'disable', 'axis'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'legendPosition',
        'displayValues',
        'comparisonValues',
        'mode',
        'stacked',
        'sort',
        'axis',
        'showFullIndicatorName',
        'featureOpacity',
      ],
    },
    line: {
      indicatorMode: 'multiple',
      dataLayerFields: ['group', 'disable', 'axis'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'legendPosition',
        'dotSize',
        'borderWidth',
        'lineTension',
        'axis',
        'displayValues',
        'showFullIndicatorName',
      ],
    },
    pie: {
      indicatorMode: 'multiple',
      dataLayerFields: ['group', 'disable'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'legendPosition',
        'innerRadius',
        'displayValues',
        'sort',
        'showFullIndicatorName',
        'featureOpacity',
      ],
    },
    polarArea: {
      indicatorMode: 'single',
      dataLayerFields: ['group', 'disable'],
      configFields: ['headline', 'colorset', 'invertColors', 'legendPosition'],
    },
    radar: {
      indicatorMode: 'multiple',
      dataLayerFields: ['group', 'disable'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'legendPosition',
        'showFullIndicatorName',
        'dotSize',
        'lineTension',
        'displayValues',
        'sort',
        'featureOpacity',
      ],
    },
    scatter: {
      indicatorMode: 'double',
      dataLayerFields: ['axis'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'displayRegression',
        'axis',
        'dotSize',
        'strokeWidth',
        'showFullIndicatorName',
      ],
    },
    bubble: {
      indicatorMode: 'triple',
      dataLayerFields: ['axis'],
      configFields: [
        'headline',
        'colorset',
        'invertColors',
        'displayRegression',
        'axis',
        'showFullIndicatorName',
        'featureOpacity',
      ],
    },
    info: {
      indicatorMode: 'multiple',
      dataLayerFields: [],
      disabledSteps: ['data', 'customize'],
      configFields: [],
    },
    teaser: {
      dataLayerFields: [],
      disabledSteps: ['indicators', 'data'],
      configFields: ['headline', 'teaser'],
    },
    text: {
      dataLayerFields: [],
      disabledSteps: ['indicators', 'data'],
      configFields: ['headline', 'text'],
    },
    adder: null,
  };

  /**
   * Determines whether a specific step can be activated for the given widget type.
   *
   * @param {WidgetType} type - The type of the widget to check.
   * @param {WidgetConfigStep} step - The configuration step to evaluate for activation.
   * @return {boolean} Returns true if the step can be activated, otherwise false.
   */
  canActivateStep(type: WidgetType, step: WidgetConfigStep): boolean {
    return !this.typeConfig[type]?.['disabledSteps']?.includes(step);
  }

  /**
   * Calculates the number of steps available for a given widget type after taking into account disabled steps.
   *
   * @param {WidgetType} type - The type of the widget for which the number of steps is being calculated.
   * @return {number} The number of steps available for the specified widget type.
   */
  numberOfSteps(type: WidgetType): number {
    return 3 - (this.typeConfig[type]?.['disabledSteps']?.length || 0);
  }

  /**
   * Generates a reactive form group or control based on the provided configuration field name and configuration object.
   * Handles multiple specific field cases such as 'map', 'teaser', 'text', etc.
   *
   * @param {string} field - The name of the configuration field for which the form structure is required.
   * @param {any} config - The configuration object containing field-specific settings.
   * @return {FormGroup|FormControl} A reactive form group or control for the specified configuration field.
   */
  getConfigFieldByName(field: string, config: any): FormGroup | FormControl {
    switch (field) {
      case 'map':
        return this.fb.group({
          tileLayer: [config.map?.tileLayer || 'Graustufen'],
          districtOpacity: [config.map?.districtOpacity || 0.7],
          poiSize: [config.map?.poiSize || 1],
          showCharts: config.map?.showCharts || false,
          isochroneProfile: [config.map?.isochroneProfile || 'walking'],
          isochroneMinutes: [config.map?.isochroneMinutes || [15, 30, 45]],
          isochroneColorset: [config.map?.isochroneColorset],
          legend: this.fb.group({
            scaleMode: [config.map?.legend?.scaleMode || 'width'],
            size: [config.map?.legend?.size],
            position: [config.map?.legend?.position || 'bottomright'],
            numberOfScales: [config.map?.legend?.numberOfScales || 6],
            legendScope: [String(config.map?.legend?.legendScope) || 'group'],
            invert: config.map?.legend.invert || false,
          }),
          captions: this.fb.group({
            key: [config.map?.captions?.key || false],
            name: [config.map?.captions?.name || false],
            value: [config.map?.captions?.value || false],
            chart: [config.map?.captions?.chart || false],
            style: [config.map?.captions?.style || 'top'],
          }),
          poi: this.fb.array([]),
        });
      case 'teaser':
        return this.fb.group({
          items: this.fb.array([]),
          autoPlay: [config?.teaser?.autoPlay || false],
        });
      case 'text':
        return this.fb.group({
          backgroundColor: [
            config.text?.backgroundColor || '#FFFFFF',
            [Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)], // Hex color validation
          ],
          alignment: [config.text?.alignment || null],
        });
      case 'scatter':
        return this.fb.group({
          displayRegression: [config.displayRegression || false],
        });
      case 'table':
        return this.fb.group({
          transpose: [config.table?.transpose],
          displayKeys: [config.table?.displayKeys],
          captionTotal: [config.table?.captionTotal],
          captionAverage: [config.table?.captionAverage],
          captionDistrict: [config.table?.captionDistrict],
          captionKey: [config.table?.captionKey],
        });
      case 'sort':
        return this.fb.group({
          enable: [config.sort?.enable || false],
          property: [config.sort?.property || 'label'],
          direction: [config.sort?.direction || 'asc'],
        });
      case 'axis':
        return this.fb.group({
          x: this.fb.group({
            min: [config.axis?.x?.min ?? null],
            max: [config.axis?.x?.max ?? null],
            label: [config.axis?.x?.label || ''],
          }),
          y: this.fb.group({
            min: [config.axis?.y?.min ?? null],
            max: [config.axis?.y?.max ?? null],
            label: [config.axis?.y?.label || ''],
          }),
        });
      default:
        return this.fb.control(config[field]);
    }
  }

  // Add similar methods for other configurations:
  getChartConfig(customConfig: any = {}): any {
    const defaultConfig = {};
    return { ...defaultConfig, ...customConfig };
  }

  getMapConfig(customConfig: any = {}): any {
    const defaultConfig = {};
    return { ...defaultConfig, ...customConfig };
  }

  getTableConfig(customConfig: any = {}): any {
    const defaultConfig = {};
    return { ...defaultConfig, ...customConfig };
  }

  getTextConfig(customConfig: any = {}): any {
    const defaultConfig = {};
    return { ...defaultConfig, ...customConfig };
  }

  /**
   * Retrieves an array of image objects with their respective source paths and copyright details.
   *
   * @return {Array.<Object>} An array where each object contains:
   * - `src` (string): The path of the image.
   * - `copyright` (string): The copyright information associated with the image.
   */
  getPoolImages(): any[] {
    const communeSegment = this.globalsStore.activeCommune()?._id || '';
    const communeImages = this.imageStore.asArray().map((image: Image) => {
      const src = getUploadPath(`imagePool/${communeSegment}`, image.src, true);

      return {
        _id: image._id,
        src: src,
        copyright: `@ ${image.source}`,
      };
    });
    const poolImages = this.poolImages.map((image: any) => {
      image._id = image.src;
      return image;
    });

    return communeImages.concat(poolImages) as TeaserImage[];
  }

  public getPoolImageBySrc(src: string): TeaserImage | undefined {
    return this.poolImages.find((image: TeaserImage) => image.src === src);
  }

  public poolImages: TeaserImage[] = [
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Stadt.jpg',
      copyright:
        '© Mathias Weil - <a target="_blank" href="https://stock.adobe.com">stock.adobe.com</a>',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Netzwerk.jpg',
      copyright:
        '© vegefox.com - <a target="_blank" href="https://stock.adobe.com">stock.adobe.com</a>',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Landschaft2.jpg',
      copyright:
        '© rammi76 - <a target="_blank" href="https://stock.adobe.com">stock.adobe.com</a>',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Siedlung.jpg',
      copyright:
        '© anatoliil - <a target="_blank" href="https://stock.adobe.com">stock.adobe.com</a>',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Laptop.jpg',
      copyright:
        '© Andrey Popov - <a target="_blank" href="https://stock.adobe.com">stock.adobe.com</a>',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Lights.jpg',
      copyright: '',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Landschaft.jpg',
      copyright: '',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hintergrund_Puzzle.jpg',
      copyright: '',
    },
    {
      _id: '',
      src: 'assets/images/teaser/AdobeStock_82903087_KONZERN_ST-LK_1240x600px_Original_78331.jpg',
      copyright: '',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Coverbild_Flyer_2017_iStock-500021351_MPL.jpg',
      copyright: 'rogerashford/iStockphoto.com;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Fotolia_70150610_X_MPL__Kekiz_ST-LK_Original_43927.jpg',
      copyright: '@ stephiera / Fotolia.com; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Fotolia_98926123_X-Duplex_ST-LK_Original_66910.jpg',
      copyright: '@ vege / Fotolia.com;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/GettyImages-551431347_M_gruen_ST-LK_Kekiz_Original_58196.jpg',
      copyright: '@ Getty Images/Fuse/Tara Moore;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Getty_Images_iStockphoto_Eucalyptys_ID80229.jpg',
      copyright: 'Getty Images/iStockphoto/Eucalyptys; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Hauser_von_oben_ST-WB_Original_49163.jpg',
      copyright: 'Alex Tuzhikov/Shutterstock.com; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/IMG_3599_bearbeitet_ST-LK_Original_42933.jpg',
      copyright: '@ Thomas Kunsch;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Kinder_auf_Spielgeruest_ST-LK_Original_60683.jpg',
      copyright: 'Shutterstock/Elizaveta Galitckaia;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Kleingeld_Kinderhaende_ST-LK_Original_79111.jpg',
      copyright: 'Marco2811/Fotolia.com; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Netz_Spielgeruest_ST-CC_Original_41026.jpg',
      copyright: 'roostler/Fotolia.com;',
    },
    {
      _id: '',
      src: 'assets/images/teaser/shutterstock_17794225_KeKiz_Titel_WB_7_ST-LK_Original_71006.jpg',
      copyright: '@ Shutterstock / raluca teodorescu; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/shutterstock_348830048_Premier_KeKiz_Titel_WB_11_orange_Original_82416.jpg',
      copyright: '@ Shutterstock / Maria Symchych; ',
    },
    {
      _id: '',
      src: 'assets/images/teaser/Shutterstock_TeerapongPromnam_ID80228.jpg',
      copyright: 'Shutterstock/TeerapongPromnam',
    },
  ];

  /**
   * returns ready to save for bookmark configuration
   * @param gridItem
   */
  transformWidgetToSavableItem(
    gridItem: CustomGridStackWidget
  ): CustomGridStackWidget {
    return {
      id: gridItem.id,
      _id: gridItem._id,
      w: gridItem.w,
      h: gridItem.h,
      x: gridItem.x,
      y: gridItem.y,
      type: gridItem.type,
      widget: {
        activeGroup: gridItem.widget.activeGroup,
        axis: gridItem.widget.axis,
        axisProperties: gridItem.widget.axisProperties,
        colorset: gridItem.widget.colorset,
        context: gridItem.widget.context,
        comparisonValues: gridItem.widget.comparisonValues,
        dataLayer: gridItem.widget.dataLayer,
        dataLayerType: gridItem.widget.dataLayerType,
        disabledDatasets: gridItem.widget.disabledDatasets,
        disabledDataPoints: gridItem.widget.disabledDataPoints,
        disabledFeatures: gridItem.widget.disabledFeatures,
        disabledCharts: gridItem.widget.disabledCharts,
        displayValues: gridItem.widget.displayValues,
        displayRegression: gridItem.widget.displayRegression,
        disabledYears: gridItem.widget.disabledYears,
        dotSize: gridItem.widget.dotSize,
        featureOpacity: gridItem.widget.featureOpacity,
        legendPosition: gridItem.widget.legendPosition,
        lineTension: gridItem.widget.lineTension,
        feature: gridItem.widget.feature,
        focusProperty: gridItem.widget.focusProperty,
        groupProperty: gridItem.widget.groupProperty,
        headline: gridItem.widget.headline,
        indicator: gridItem.widget.indicator,
        indicators: gridItem.widget.indicators,
        innerRadius: gridItem.widget.innerRadius,
        invertColors: gridItem.widget.invertColors,
        locked: gridItem.widget.locked,
        map: gridItem.widget.map,
        mode: gridItem.widget.mode,
        radiusProperty: gridItem.widget.radiusProperty,
        showFullIndicatorName: gridItem.widget.showFullIndicatorName,
        stacked: gridItem.widget.stacked,
        sort: gridItem.widget.sort,
        table: gridItem.widget.table,
        teaser: gridItem.widget.teaser,
        text: gridItem.widget.text,
        wegweiserData: gridItem.widget.wegweiserData,
        wegweiserRequestParams: gridItem.widget.wegweiserRequestParams,
        year: gridItem.widget.year,
      },
    };
  }
}
